/**
 * Engine for timeseries linear x-axis ticks (epoch ms + manual ticktext).
 *
 * Plotly receives monotonic numeric coordinates; local wall-clock labels and
 * conditional (UTC+/-offset) suffixes are computed outside Plotly.
 *
 * @packageDocumentation
 * @module TimeseriesAxisTicks
 * @public
 */

import {
  DEFAULT_TIMESERIES_AXIS_LABEL_MODE,
  TimeseriesAxisLabelMode,
} from '../config/plotTypes';
import { buildAmbiguityTimeKey, formatTickLabel, getLocalCalendarParts, resolveGranularityFromStepMs, resolveTickLabelDetail } from './timeseriesTickLabelFormat';

// -- type definitions --

/** Absolute epoch millisecond range `[minMs, maxMs]`. */
export type EpochMillisecondRange = readonly [number, number];

/** Blank x-axis hover format that suppresses Plotly's automatic date header. */
export const TIMESERIES_XAXIS_HOVERFORMAT = ' ';

/** Paper-space y position for the timezone annotation without a range slider. */
const TIMEZONE_ANNOTATION_Y_WITHOUT_SLIDER = -0.16;

/** Pixel shift below plot bottom when the range slider is active. */
const TIMEZONE_ANNOTATION_YSHIFT_WITH_SLIDER = -18;

/** Bottom margin (px) reserved for the timezone annotation without a range slider. */
export const TIMESERIES_TIMEZONE_LABEL_BOTTOM_MARGIN = 22;

/** Plotly manual tick payload for a linear x-axis. */
export interface TimeseriesAxisTicks {
  readonly tickvals: readonly number[];
  readonly ticktext: readonly string[];
}

/** Granularity bucket used when choosing tick spacing and label formatting. */
export type TimeseriesTickGranularity =
  | 'millisecond'
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'month'
  | 'year';

/** Compact vs extended contextual label detail (Plotly-style). */
export type TimeseriesTickLabelDetail = 'compact' | 'extended';

/** Local label metadata for one tick position. */
export interface TimeseriesTickLabelEntry {
  readonly epochMs: number;
  readonly granularity: TimeseriesTickGranularity;
  readonly detail: TimeseriesTickLabelDetail;
  /** Ambiguity key for DST suffix rules (`HH:mm` or `HH:mm:ss`). */
  readonly ambiguityTimeKey: string;
  readonly offsetSuffix: string;
  /** Local wall-clock label without UTC offset suffix. */
  readonly localTimeLabel: string;
}

/** Index of local labels keyed by tick epoch ms. */
export type TimeseriesTickLabelIndex = Readonly<
  Record<number, TimeseriesTickLabelEntry>
>;

/** Input for the main tick builder. */
export interface BuildTimeseriesAxisTicksParams {
  readonly visibleRangeMs: EpochMillisecondRange;
  readonly displayTimezone: string;
  readonly labelMode?: TimeseriesAxisLabelMode;
  /** BCP 47 locale tag(s) for tick labels; defaults to runtime locale. */
  readonly locale?: string | string[];
}

/** Input for relayout-driven tick regeneration. */
export type RebuildTimeseriesAxisTicksParams = BuildTimeseriesAxisTicksParams;

/** Partial Plotly x-axis patch for timeseries linear mode. */
export interface TimeseriesLinearXAxisLayoutPatch {
  readonly type: 'linear';
  readonly tickmode: 'array';
  readonly tickvals: readonly number[];
  readonly ticktext: readonly string[];
  readonly showspikes: true;
  readonly spikemode: 'across';
}


/** Input for {@link buildLocalLabelIndex}. */
export interface BuildLocalLabelIndexParams {
  readonly tickValsMs: readonly number[];
  readonly displayTimezone: string;
  readonly granularity: TimeseriesTickGranularity;
  readonly locale?: string | string[];
}

// -- constants --

export const DEFAULT_TICK_STEP_TARGET = 5;

const SECONDS_IN_MS = 1000;
const MINUTES_IN_MS = 60 * SECONDS_IN_MS;
const HOURS_IN_MS = 60 * MINUTES_IN_MS;
const DAYS_IN_MS = 24 * HOURS_IN_MS;
const MONTHS_IN_MS = 30 * DAYS_IN_MS;
const YEARS_IN_MS = 365 * DAYS_IN_MS;

const DEFAULT_TICK_STEP_MS = DAYS_IN_MS;

const TICK_STEPS: readonly number[] = [
  100,
  200,
  500,
  SECONDS_IN_MS,
  2 * SECONDS_IN_MS,
  5 * SECONDS_IN_MS,
  10 * SECONDS_IN_MS,
  15 * SECONDS_IN_MS,
  30 * SECONDS_IN_MS,
  MINUTES_IN_MS,
  2 * MINUTES_IN_MS,
  5 * MINUTES_IN_MS,
  10 * MINUTES_IN_MS,
  15 * MINUTES_IN_MS,
  30 * MINUTES_IN_MS,
  HOURS_IN_MS,
  2 * HOURS_IN_MS,
  4 * HOURS_IN_MS,
  6 * HOURS_IN_MS,
  12 * HOURS_IN_MS,
  DAYS_IN_MS,
  2 * DAYS_IN_MS,
  5 * DAYS_IN_MS,
  7 * DAYS_IN_MS,
  30 * DAYS_IN_MS,
  MONTHS_IN_MS,
  2 * MONTHS_IN_MS,
  5 * MONTHS_IN_MS,
  7 * MONTHS_IN_MS,
  12 * MONTHS_IN_MS,
  YEARS_IN_MS,
  2 * YEARS_IN_MS,
  5 * YEARS_IN_MS,
  10 * YEARS_IN_MS,
];

const DEFAULT_OFFSET_STRING = 'UTC';

// -- helper functions --

/**
 * Converts absolute epoch milliseconds to a UTC ISO-8601 string.
 *
 * @param epochMs - Milliseconds since Unix epoch.
 * @returns UTC ISO string suitable for host callbacks.
 */
export function epochMsToIso(epochMs: number): string {
  return new Date(epochMs).toISOString();
}

/**
 * Converts an epoch millisecond range to ISO strings for external consumers.
 *
 * @param rangeMs - Visible x-axis range in epoch milliseconds.
 * @returns ISO strings `[startIso, endIso]`.
 */
export function epochRangeMsToIsoRange(
  rangeMs: EpochMillisecondRange
): readonly [string, string] {
  return [epochMsToIso(rangeMs[0]), epochMsToIso(rangeMs[1])];
}

/**
 * Extracts numeric x-axis range limits from a Plotly relayout event.
 *
 * Accepts epoch-ms numbers and ISO date strings (Plotly `date` axes may emit either).
 *
 * @param event - Plotly relayout payload.
 * @returns Epoch millisecond range, or `undefined` when x-axis range is absent.
 */
export function parseRelayoutXRangeMs(
  event: Readonly<Record<string, unknown>>
): EpochMillisecondRange | undefined {
  const indexedRange = parseEpochMillisecondRange([
    event[`xaxis.range[0]`],
    event[`xaxis.range[1]`],
  ]);
  if (indexedRange) {
    return indexedRange;
  }

  return parseEpochMillisecondRange(event['xaxis.range']);
}

/**
 * Maps sorted UTC instants to monotonic epoch milliseconds for Plotly traces.
 *
 * @param instants - Sorted measurement instants from {@link PlotDataInput}.
 * @returns Epoch milliseconds preserving input order.
 */
export function mapInstantsToEpochMs(
  instants: readonly Date[]
): readonly number[] {
  return instants.map((instant) => instant.getTime()).sort((a, b) => a - b);
}

/**
 * Selects tick spacing from the visible range width.
 *
 * Aims for roughly {@link DEFAULT_TICK_STEP_TARGET} ticks in the visible range.
 *
 * @param rangeMs - Visible x-axis range in epoch milliseconds.
 * @returns Tick step in milliseconds.
 */
export function selectTickStepMs(rangeMs: EpochMillisecondRange): number {
  const [start, end] = rangeMs;
  const duration = end - start;

  const targetTicks = DEFAULT_TICK_STEP_TARGET;
  const roughStep = duration / targetTicks;

  const selectedStep = TICK_STEPS.find((step) => step >= roughStep);
  return selectedStep !== undefined ? selectedStep : DEFAULT_TICK_STEP_MS;
}

/**
 * Generates monotonic tick positions across a visible range.
 *
 * @param rangeMs - Visible x-axis range in epoch milliseconds.
 * @param stepMs - Tick step in milliseconds.
 * @returns Sorted tick epoch millisecond values.
 */
export function generateTickValuesMs(
  rangeMs: EpochMillisecondRange,
  stepMs: number
): readonly number[] {
  const [start, end] = rangeMs;

  if (start >= end || stepMs <= 0) {
    return [];
  }

  const firstTick = Math.ceil(start / stepMs) * stepMs;

  const ticks: number[] = [];
  let currentTick = firstTick;

  while (currentTick <= end) {
    ticks.push(currentTick);
    currentTick += stepMs;
  }

  return ticks;
}

/**
 * Formats the `(UTC±offset)` suffix for one absolute instant in a timezone.
 *
 * @param epochMs - Tick position in epoch milliseconds.
 * @param displayTimezone - IANA timezone used for local display.
 * @returns Offset suffix such as `(UTC+2)`.
 */
export function formatUtcOffsetSuffix(
  epochMs: number,
  displayTimezone: string
): string {
  const offsetString = Intl.DateTimeFormat('ia', {
    timeZoneName: 'short',
    timeZone: displayTimezone,
  })
    .formatToParts(new Date(epochMs))
    .find((i) => i.type === 'timeZoneName')?.value;

  if (!offsetString) return `(${DEFAULT_OFFSET_STRING})`;

  return `(${offsetString.replace('GMT', 'UTC')})`;
}

/**
 * Builds per-tick local label metadata for ambiguity analysis and ticktext.
 *
 * @param params - Tick positions, timezone, granularity, and locale.
 * @returns Immutable index keyed by tick epoch ms.
 */
export function buildLocalLabelIndex(
  params: BuildLocalLabelIndexParams
): TimeseriesTickLabelIndex {
  return params.tickValsMs.reduce((acc: TimeseriesTickLabelIndex, tickValMs, index, array) => {
    const detail = resolveTickLabelDetail({
      granularity: params.granularity,
      isFirstTick: index === 0,
      previousParts: index > 0 ? getLocalCalendarParts(array[index - 1], params.displayTimezone) : undefined,
      currentParts: getLocalCalendarParts(tickValMs, params.displayTimezone),
    });
    const parts = getLocalCalendarParts(tickValMs, params.displayTimezone);
    const ambiguityTimeKey = buildAmbiguityTimeKey(parts, params.granularity);
    const offsetSuffix = formatUtcOffsetSuffix(tickValMs, params.displayTimezone);
    const localTimeLabel = formatTickLabel({
      epochMs: tickValMs,
      displayTimezone: params.displayTimezone,
      granularity: params.granularity,
      detail,
      locale: params.locale,
    });
    return {
      ...acc,
      [tickValMs]: {
        epochMs: tickValMs,
        granularity: params.granularity,
        detail,
        ambiguityTimeKey,
        offsetSuffix,
        localTimeLabel,
      },
    };
  }, {} as TimeseriesTickLabelIndex);
}

/**
 * Finds ambiguity keys that appear with more than one UTC offset in visible ticks.
 *
 * @param labelIndex - Per-tick local label metadata.
 * @returns Set of ambiguous time keys requiring conditional suffixes.
 */
export function findAmbiguousTimeKeys(
  labelIndex: TimeseriesTickLabelIndex
): ReadonlySet<string> {
  const offsetsByKey = new Map<string, Set<string>>();

  for (const entry of Object.values(labelIndex)) {
    if (entry.ambiguityTimeKey === '') {
      continue;
    }

    const offsets =
      offsetsByKey.get(entry.ambiguityTimeKey) ?? new Set<string>();
    offsets.add(entry.offsetSuffix);
    offsetsByKey.set(entry.ambiguityTimeKey, offsets);
  }

  const result = new Set<string>();
  for (const [key, offsets] of offsetsByKey) {
    if (offsets.size > 1) {
      result.add(key);
    }
  }

  return result;
}

/**
 * Formats one tick label according to label mode and ambiguity context.
 *
 * @param entry - Local label metadata for the tick.
 * @param labelMode - Conditional or global suffix mode.
 * @param ambiguousTimeKeys - Ambiguous local time keys in the visible range.
 * @returns Final tick text shown on the x-axis.
 */
export function formatTickText(
  entry: TimeseriesTickLabelEntry,
  labelMode: TimeseriesAxisLabelMode,
  ambiguousTimeKeys: ReadonlySet<string>
): string {
  const baseText = entry.localTimeLabel;
  const ambiguityKey = entry.ambiguityTimeKey;
  const offsetSuffix = entry.offsetSuffix;
  const isAmbiguous = ambiguousTimeKeys.has(ambiguityKey);
  const alwaysAddSuffix = labelMode === 'global';
  if (alwaysAddSuffix || isAmbiguous) {
    return `${baseText} ${offsetSuffix}`;
  }
  return baseText;
}

/**
 * Builds parallel tick value/text arrays for Plotly manual ticks.
 *
 * @param params - Visible range, timezone, and label mode.
 * @returns Plotly-compatible tick payload.
 */
export function buildTimeseriesAxisTicks(
  params: BuildTimeseriesAxisTicksParams
): TimeseriesAxisTicks {
  const tickStepMs = selectTickStepMs(params.visibleRangeMs);
  const labelMode = resolveTimeseriesAxisLabelMode(params.labelMode);
  const tickvals = generateTickValuesMs(params.visibleRangeMs, tickStepMs);
  const granularity = resolveGranularityFromStepMs(tickStepMs);
  const timeSeriesTickLabelIndex = buildLocalLabelIndex({
    tickValsMs: tickvals,
    displayTimezone: params.displayTimezone,
    granularity,
    locale: params.locale,
  });
  const ambiguousTimeKeys = findAmbiguousTimeKeys(timeSeriesTickLabelIndex);
  const ticktext = tickvals.map(
    (tickValMs) => formatTickText(
      timeSeriesTickLabelIndex[tickValMs],
      labelMode,
      ambiguousTimeKeys
    )
  );
  return { tickvals, ticktext };
}

/**
 * Rebuilds axis ticks after relayout using the new visible numeric range.
 * 
 * @TODO: deprecate and remove this function
 *
 * @param params - Relayout-derived range and display settings.
 * @returns Plotly-compatible tick payload.
 */
export const rebuildTimeseriesAxisTicks: (
  params: RebuildTimeseriesAxisTicksParams
) => TimeseriesAxisTicks = buildTimeseriesAxisTicks;

/**
 * Builds the Plotly x-axis patch for timeseries linear mode.
 *
 * @param ticks - Manual tick payload.
 * @returns Partial x-axis layout fields.
 */
export function buildTimeseriesLinearXAxisPatch(
  ticks: TimeseriesAxisTicks
): TimeseriesLinearXAxisLayoutPatch {
  return {
    type: 'linear',
    tickmode: 'array',
    tickvals: ticks.tickvals,
    ticktext: ticks.ticktext,
    showspikes: true,
    spikemode: 'across',
  };
}

/**
 * Merges manual ticks into an existing Plotly layout without mutating it.
 *
 * @param layout - Base Plotly layout.
 * @param ticks - Manual tick payload.
 * @returns New layout including x-axis tick arrays.
 */
export function mergeTimeseriesAxisTicksIntoLayout(
  layout: Partial<Plotly.Layout>,
  ticks: TimeseriesAxisTicks
): Partial<Plotly.Layout> {
  const { type, tickmode, showspikes, spikemode } =
    buildTimeseriesLinearXAxisPatch(ticks);

  return {
    ...layout,
    xaxis: {
      ...(layout.xaxis ?? {}),
      type,
      tickmode,
      tickvals: [...ticks.tickvals],
      ticktext: [...ticks.ticktext],
      showspikes,
      spikemode,
    },
  };
}

/**
 * Builds a compact timezone annotation for the chart chrome.
 *
 * @param displayTimezone - Effective IANA timezone for the plot.
 * @param plotType - Current plot display mode.
 * @param showRangeSlider - Whether the timeseries range slider is visible.
 * @returns Plotly layout fragment with a timezone annotation.
 */
export function buildTimeseriesTimezoneAnnotation(
  displayTimezone: string,
  plotType: 'timeseries' | 'scatter',
  showRangeSlider = false
): Partial<Plotly.Layout> {
  if (plotType !== 'timeseries') {
    return {};
  }

  return {
    annotations: [
      {
        text: `Fuseau horaire: ${displayTimezone}`,
        xref: 'paper',
        yref: 'paper',
        x: 1,
        y: showRangeSlider ? 0 : TIMEZONE_ANNOTATION_Y_WITHOUT_SLIDER,
        xanchor: 'right',
        yanchor: 'top',
        ...(showRangeSlider
          ? { yshift: TIMEZONE_ANNOTATION_YSHIFT_WITH_SLIDER }
          : {}),
        showarrow: false,
        font: { size: 11, color: '#666' },
      },
    ],
  };
}

/**
 * Merges timezone annotation metadata into an existing Plotly layout.
 *
 * @param layout - Base Plotly layout.
 * @param displayTimezone - Effective IANA timezone for the plot.
 * @param plotType - Current plot display mode.
 * @param showRangeSlider - Whether the timeseries range slider is visible.
 * @returns New layout including the timezone label when applicable.
 */
export function withTimeseriesTimezoneAnnotation(
  layout: Partial<Plotly.Layout>,
  displayTimezone: string,
  plotType: 'timeseries' | 'scatter',
  showRangeSlider = false
): Partial<Plotly.Layout> {
  const annotationPatch = buildTimeseriesTimezoneAnnotation(
    displayTimezone,
    plotType,
    showRangeSlider
  );

  if (!annotationPatch.annotations?.length) {
    return layout;
  }

  return {
    ...layout,
    annotations: [
      ...(layout.annotations ?? []),
      ...annotationPatch.annotations,
    ],
  };
}

/**
 * Resolves the visible x-axis range in epoch ms from layout state.
 *
 * @param layoutRange - Current Plotly x-axis range value.
 * @param fallbackRangeMs - Fallback when layout range is absent or invalid.
 * @returns Visible range in epoch milliseconds.
 */
export function resolveVisibleTimeseriesRangeMs(
  layoutRange: unknown,
  fallbackRangeMs?: EpochMillisecondRange
): EpochMillisecondRange | undefined {
  const parsedLayoutRange = parseEpochMillisecondRange(layoutRange);
  if (parsedLayoutRange) {
    return parsedLayoutRange;
  }

  return parseEpochMillisecondRange(fallbackRangeMs);
}

function parseEpochMillisecondRange(
  range: unknown
): EpochMillisecondRange | undefined {
  if (!Array.isArray(range) || range.length !== 2) {
    return undefined;
  }

  const start = toEpochMs(range[0]);
  const end = toEpochMs(range[1]);

  if (start === undefined || end === undefined) {
    return undefined;
  }

  return [start, end];
}

function toEpochMs(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  if (value instanceof Date) {
    const parsed = value.getTime();
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

/**
 * Resolves the effective label mode from explicit input or library default.
 *
 * @param labelMode - Optional preference value.
 * @returns Resolved label mode.
 */
export function resolveTimeseriesAxisLabelMode(
  labelMode?: TimeseriesAxisLabelMode
): TimeseriesAxisLabelMode {
  return labelMode ?? DEFAULT_TIMESERIES_AXIS_LABEL_MODE;
}
